import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { RFQ_STAGES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useBulkSelect, SelectAllCheckbox, BulkBar } from "@/components/BulkBar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Trash2, DollarSign, FolderOpen, Send, FileCheck2, Clock, Users, User, Download, Trophy, ClipboardList, Pencil, CheckCircle2, BarChart3 } from "lucide-react";
import RFQReports from "@/components/RFQReports";
import { toast } from "sonner";
import CreateRFQDialog from "@/pages/CreateRFQDialog";
import ImportHistoryDialog from "@/components/ImportHistoryDialog";
import { PaginationBar, PAGE_SIZE } from "@/components/Pagination";

const fmtValue = (obj) => {
  if (!obj || !Object.keys(obj).length) return "$0";
  return Object.entries(obj).map(([c, v]) => `${c} ${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`).join(" · ");
};
const moneyCur = (obj, cur) => `${cur} ${Number((obj && obj[cur]) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const KPIS = [
  { key: "total", label: "Total RFQs", icon: ClipboardList, tint: "text-[#1B4383] bg-[#E6ECF5]", value: (s) => s.total, sub: (s) => `${s.open} Open` },
  { key: "pipeline", label: "Total Pipeline", icon: DollarSign, tint: "text-emerald-600 bg-emerald-50", money: true, value: (s, cur) => moneyCur(s.quoted_value, cur), sub: (s, cur) => `${moneyCur(s.won_value, cur)} Won` },
  { key: "due_soon", label: "Due ≤ 7 Days", icon: Clock, tint: "text-rose-600 bg-rose-50", value: (s) => s.due_soon, sub: () => "Deadline Approaching" },
  { key: "quoting", label: "In Progress", icon: Send, tint: "text-sky-600 bg-sky-50", value: (s) => s.quoting, sub: (s) => `${s.quoted} Awaiting Decision` },
  { key: "won", label: "Won (Closed)", icon: Trophy, tint: "text-amber-600 bg-amber-50", money: true, value: (s, cur) => moneyCur(s.won_value, cur), sub: (s) => `${s.won} Won · ${s.lost} Lost` },
];

export default function RFQList() {
  const [rfqs, setRfqs] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [members, setMembers] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ search: "", client_id: "all", supplier_id: "all", owner_id: "all", industry: "all", stage: "all", date_from: "", date_to: "", added_from: "", added_to: "" });
  const [view, setView] = useState("active");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [scope, setScope] = useState("team");
  const [stats, setStats] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currency, setCurrency] = useState("USD");
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const sel = useBulkSelect();
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkStage, setBulkStage] = useState("none");
  const [bulkOwner, setBulkOwner] = useState("none");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [page, setPage] = useState(1);
  const location = useLocation();
  // Clicking the RFQs nav link (even while already here) resets to the fresh first-open view.
  useEffect(() => { setPage(1); window.scrollTo(0, 0); }, [location.key]);
  const paged = rfqs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const visibleIds = paged.map((r) => r.id);

  const load = () => {
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.client_id !== "all") params.client_id = filters.client_id;
    if (filters.supplier_id !== "all") params.supplier_id = filters.supplier_id;
    if (filters.owner_id !== "all") params.owner_id = filters.owner_id;
    if (filters.industry !== "all") params.industry = filters.industry;
    if (filters.stage !== "all") params.stage = filters.stage;
    else if (view) params.view = view;
    if (view === "reports") return;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.added_from) params.added_from = filters.added_from;
    if (filters.added_to) params.added_to = filters.added_to;
    if (scope === "mine") params.owner = "me";
    api.get("/rfqs", { params }).then((r) => { setRfqs(r.data); setPage(1); });
    api.get("/rfqs/stats", { params: { scope } }).then((r) => setStats(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/team-members").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/industries").then((r) => setIndustries(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, scope, view, refreshKey]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const remove = async (e, r) => {
    e.stopPropagation();
    if (!window.confirm(`Delete RFQ ${r.code || r.title}? This removes its items, supplier assignments and quote. This cannot be undone.`)) return;
    try { await api.delete(`/rfqs/${r.id}`); toast.success("RFQ deleted"); load(); }
    catch { toast.error("Could not delete RFQ"); }
  };

  const bulkDelete = async () => {
    const ids = [...sel.selected];
    if (!window.confirm(`Delete ${ids.length} RFQ${ids.length !== 1 ? "s" : ""}? This removes their items, supplier assignments and quotes. This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/rfqs/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} RFQ${data.deleted !== 1 ? "s" : ""}`);
      sel.clear(); load();
    } catch { toast.error("Bulk delete failed"); } finally { setBulkBusy(false); }
  };

  const bulkApply = async () => {
    const ids = [...sel.selected];
    const body = { ids };
    if (bulkStage !== "none") body.stage = bulkStage;
    if (bulkOwner !== "none") body.owner_id = bulkOwner;
    if (!body.stage && !body.owner_id) { toast.error("Choose a stage or owner to apply."); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/rfqs/bulk-update", body);
      toast.success(`Updated ${data.updated} RFQ${data.updated !== 1 ? "s" : ""}`);
      setBulkOpen(false); setBulkStage("none"); setBulkOwner("none"); sel.clear(); load();
    } catch { toast.error("Bulk update failed"); } finally { setBulkBusy(false); }
  };

  return (
    <AppLayout title="RFQs" actions={
      <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-2">
        <div className="flex w-full rounded-lg border border-border bg-white p-0.5 sm:w-auto" data-testid="scope-toggle">
          <button onClick={() => setScope("team")} data-testid="scope-team"
            className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors sm:flex-none ${scope === "team" ? "bg-[#1B4383] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            <Users className="h-4 w-4" />Team
          </button>
          <button onClick={() => setScope("mine")} data-testid="scope-mine"
            className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors sm:flex-none ${scope === "mine" ? "bg-[#1B4383] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            <User className="h-4 w-4" />Mine
          </button>
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:flex-nowrap">
          <Button variant="outline" className="flex-1 sm:flex-none" onClick={async () => {
            try {
              const res = await api.get("/rfqs/export", { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement("a"); a.href = url; a.download = `somcore_rfqs_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
              setTimeout(() => window.URL.revokeObjectURL(url), 30000);
            } catch { toast.error("Export failed"); }
          }} data-testid="export-rfqs-button">
            <Download className="mr-2 h-4 w-4" />Export
          </Button>
          <Button variant="outline" className="flex-1 sm:flex-none" onClick={() => setImportOpen(true)} data-testid="import-history-button">
            <FolderOpen className="mr-2 h-4 w-4" />Import History
          </Button>
          <Button className="flex-1 sm:flex-none" onClick={() => setOpen(true)} data-testid="create-rfq-button">
            <Plus className="mr-2 h-4 w-4" />New RFQ
          </Button>
        </div>
      </div>
    }>
      <div className="mb-3 flex items-center justify-end gap-2">
        <span className="text-xs font-medium text-muted-foreground">Currency</span>
        <div className="flex rounded-md border border-border p-0.5" data-testid="kpi-currency-toggle">
          {["USD", "TZS"].map((c) => (
            <button key={c} onClick={() => setCurrency(c)} data-testid={`kpi-cur-${c}`}
              className={`rounded px-2.5 py-1 text-xs font-medium ${currency === c ? "bg-brand text-white" : "text-muted-foreground hover:text-slate-900"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="rfq-kpis">
        {KPIS.map((k, idx) => {
          const val = stats ? k.value(stats, currency) : "—";
          const lastOdd = idx === KPIS.length - 1 && KPIS.length % 2 === 1;
          return (
            <div key={k.key} className={`rounded-lg border border-border bg-white p-4 ${lastOdd ? "col-span-2 lg:col-span-1" : ""}`} data-testid={`kpi-${k.key}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[11px] font-semibold tracking-wide text-slate-400">{k.label}</span>
                <span className={`flex h-7 w-7 items-center justify-center rounded-md ${k.tint}`}><k.icon className="h-4 w-4" /></span>
              </div>
              <div className={`font-heading font-bold text-slate-900 ${k.money ? "text-lg" : "text-2xl"}`}>{val}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{stats && k.sub ? k.sub(stats, currency) : "\u00a0"}</div>
            </div>
          );
        })}
      </div>

      <div className="mb-4 inline-flex rounded-lg border border-border bg-white p-1" data-testid="rfq-view-toggle">
        <button onClick={() => setView("active")} data-testid="rfq-view-active" className={`inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${view === "active" ? "bg-[#1B4383] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          Active{stats ? ` (${(stats.new ?? 0) + (stats.quoting ?? 0)})` : ""}
        </button>
        <button onClick={() => setView("completed")} data-testid="rfq-view-completed" className={`inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${view === "completed" ? "bg-[#1B4383] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <CheckCircle2 className="h-4 w-4" />Completed{stats ? ` (${(stats.quoted ?? 0) + (stats.won ?? 0) + (stats.lost ?? 0) + (stats.unquoted ?? 0)})` : ""}
        </button>
        <button onClick={() => setView("reports")} data-testid="rfq-view-reports" className={`inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${view === "reports" ? "bg-[#1B4383] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <BarChart3 className="h-4 w-4" />Reports
        </button>
      </div>

      {view === "reports" ? <RFQReports /> : (<>
      <div className="mb-4 rounded-lg border border-border bg-white p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); set("search", q.trim()); }}>
              <Input className="pl-9" placeholder="Search Title, SOM-REQ / SOM-RFQ Or Client Ref…" value={q} onChange={(e) => { setQ(e.target.value); if (!e.target.value) set("search", ""); }} data-testid="rfq-search-input" />
              <Button type="submit" className="shrink-0 bg-[#1B4383] hover:bg-[#16386e]" data-testid="rfq-search-enter">Enter</Button>
            </form>
          </div>
          <Select value={filters.client_id} onValueChange={(v) => set("client_id", v)}>
            <SelectTrigger data-testid="filter-client"><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Clients</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.owner_id} onValueChange={(v) => set("owner_id", v)}>
            <SelectTrigger data-testid="filter-owner"><SelectValue placeholder="Owner" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Owners</SelectItem>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name || m.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.industry} onValueChange={(v) => set("industry", v)}>
            <SelectTrigger data-testid="filter-industry"><SelectValue placeholder="Industry" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Industries</SelectItem>{industries.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.supplier_id} onValueChange={(v) => set("supplier_id", v)}>
            <SelectTrigger data-testid="filter-supplier"><SelectValue placeholder="Supplier" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Suppliers</SelectItem>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="mt-3 flex flex-col gap-3 border-t border-border pt-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
          <Select value={filters.stage} onValueChange={(v) => set("stage", v)}>
            <SelectTrigger className="w-full sm:w-40" data-testid="filter-stage"><SelectValue placeholder="Stage" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Stages</SelectItem>{RFQ_STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <span className="text-sm font-medium text-slate-600 sm:ml-2">Received Between</span>
          <div className="flex items-center gap-2">
            <Input type="date" className="min-w-0 flex-1 sm:w-auto sm:flex-none" value={filters.date_from} max={filters.date_to || undefined} onChange={(e) => set("date_from", e.target.value)} data-testid="filter-date-from" />
            <span className="text-sm text-muted-foreground">and</span>
            <Input type="date" className="min-w-0 flex-1 sm:w-auto sm:flex-none" value={filters.date_to} min={filters.date_from || undefined} onChange={(e) => set("date_to", e.target.value)} data-testid="filter-date-to" />
          </div>
          <span className="text-sm font-medium text-slate-600 sm:ml-2">REQ Added Between</span>
          <div className="flex items-center gap-2">
            <Input type="date" className="min-w-0 flex-1 sm:w-auto sm:flex-none" value={filters.added_from} max={filters.added_to || undefined} onChange={(e) => set("added_from", e.target.value)} data-testid="filter-added-from" />
            <span className="text-sm text-muted-foreground">and</span>
            <Input type="date" className="min-w-0 flex-1 sm:w-auto sm:flex-none" value={filters.added_to} min={filters.added_from || undefined} onChange={(e) => set("added_to", e.target.value)} data-testid="filter-added-to" />
          </div>
          {(filters.date_from || filters.date_to || filters.added_from || filters.added_to) && (
            <Button variant="ghost" size="sm" className="self-start" onClick={() => setFilters((f) => ({ ...f, date_from: "", date_to: "", added_from: "", added_to: "" }))} data-testid="filter-date-clear">Clear dates</Button>
          )}
        </div>
      </div>

      <div className="space-y-2.5 sm:hidden" data-testid="rfq-cards">
        {paged.map((r) => (
          <div key={r.id} role="button" tabIndex={0} onClick={() => sel.selected.size > 0 ? sel.toggle(r.id) : navigate(`/rfqs/${r.id}`)} className={`rounded-lg border border-border p-4 ${sel.isSelected(r.id) ? "bg-[#EEF3FA]" : "bg-white"}`} data-testid={`rfq-card-${r.id}`}>
            <div className="flex items-start gap-3">
              <div onClick={(e) => e.stopPropagation()} className="shrink-0 pt-0.5"><Checkbox checked={sel.isSelected(r.id)} onCheckedChange={() => sel.toggle(r.id)} aria-label="Select RFQ" data-testid={`bulk-select-rfq-card-${r.id}`} /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-primary">{r.code || "—"}</span>{r.is_stock && <span className="ml-1.5 rounded-full bg-[#FBF6E9] px-1.5 py-0.5 text-[10px] font-semibold text-[#A87F31]">Stock</span>}
                  {/SOM-RFQ/i.test(filters.search) && r.code && <span className="block text-[10px] text-slate-500">Previously Sent To Suppliers As {r.code.replace(/^SOM-REQ-/, "SOM-RFQ-")}</span>}
                  <StatusBadge status={r.stage || "new"} />
                </div>
                <div className="mt-1 break-words font-medium leading-snug text-slate-900">{r.title}</div>
                <div className="mt-0.5 break-words text-xs text-muted-foreground">{r.client_name}{r.owner_name ? ` · ${r.owner_name}` : ""}</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 border-t border-border pt-3">
              <div>
                <div className="text-[11px] tracking-wide text-muted-foreground">Received</div>
                <div className="mt-0.5 font-mono text-xs text-slate-700">{r.date_received || "—"}</div>
              </div>
              <div>
                <div className="text-[11px] tracking-wide text-muted-foreground">Deadline</div>
                <div className="mt-0.5 font-mono text-xs text-slate-700">{r.quote_deadline || "—"}</div>
              </div>
            </div>
            {(r.quoted_value_usd || r.quoted_value_tzs) ? (
              <div className="mt-2.5 flex items-center justify-between border-t border-border pt-2.5">
                <span className="text-[11px] tracking-wide text-muted-foreground">Quoted Value</span>
                <span className="font-mono text-xs font-semibold text-slate-900">
                  {r.quoted_value_usd ? `USD ${Number(r.quoted_value_usd).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : `TZS ${Number(r.quoted_value_tzs).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                </span>
              </div>
            ) : null}
          </div>
        ))}
        {!rfqs.length && <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No RFQs found.</div>}
      </div>

      <div className="hidden rounded-lg border border-border bg-white overflow-x-auto sm:block">
        <table className="data-table w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs tracking-wide text-slate-500">
              <th className="w-10 px-5 py-3"><SelectAllCheckbox ids={visibleIds} selected={sel.selected} onToggleAll={sel.toggleAll} /></th>
              <th className="px-5 py-3 font-medium">Somcore Ref</th>
              <th className="px-5 py-3 font-medium">Client RFQ Ref</th>
              <th className="px-5 py-3 font-medium">Title</th>
              <th className="px-5 py-3 font-medium">Client</th>
              <th className="px-5 py-3 font-medium">Owner</th>
              <th className="px-5 py-3 font-medium">Received</th>
              <th className="px-5 py-3 font-medium">Deadline</th>
              <th className="px-5 py-3 font-medium">Date Quoted</th>
              <th className="px-5 py-3 font-medium text-right">Quoted Value (TZS)</th>
              <th className="px-5 py-3 font-medium text-right">Quoted Value (USD)</th>
              <th className="px-5 py-3 font-medium text-center">Stage</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((r) => (
              <tr key={r.id} onClick={() => sel.selected.size > 0 ? sel.toggle(r.id) : navigate(`/rfqs/${r.id}`)} className={`cursor-pointer border-t border-border hover:bg-slate-50 ${sel.isSelected(r.id) ? "bg-[#EEF3FA]" : ""}`} data-testid={`rfq-row-${r.id}`}>
                <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={sel.isSelected(r.id)} onCheckedChange={() => sel.toggle(r.id)} aria-label="Select RFQ" data-testid={`bulk-select-rfq-${r.id}`} />
                </td>
                <td className="px-5 py-3 font-mono text-xs text-primary">{r.code || "—"}{r.is_stock && <span className="ml-1.5 rounded-full bg-[#FBF6E9] px-1.5 py-0.5 font-sans text-[10px] font-semibold text-[#A87F31]" data-testid={`rfq-stock-${r.id}`}>Stock</span>}{/SOM-RFQ/i.test(filters.search) && r.code && <span className="block font-sans text-[10px] text-slate-500" data-testid={`rfq-sent-as-${r.id}`}>Previously Sent To Suppliers As {r.code.replace(/^SOM-REQ-/, "SOM-RFQ-")}</span>}</td>
                <td className="px-5 py-3 font-mono text-xs text-muted-foreground">{r.client_ref || "—"}</td>
                <td className="px-5 py-3 font-medium text-slate-900">{r.title}</td>
                <td className="px-5 py-3 text-muted-foreground">{r.client_name}</td>
                <td className="px-5 py-3 text-muted-foreground">{r.owner_name || "—"}</td>
                <td className="px-5 py-3 font-mono text-xs">{r.date_received || "—"}</td>
                <td className="px-5 py-3 font-mono text-xs">{r.quote_deadline || "—"}</td>
                <td className="px-5 py-3 font-mono text-xs">{r.quoted_date || "—"}</td>
                <td className="px-5 py-3 text-right font-mono text-xs text-slate-700">{r.quoted_value_tzs ? `TZS ${Number(r.quoted_value_tzs).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}</td>
                <td className="px-5 py-3 text-right font-mono text-xs text-slate-700">{r.quoted_value_usd ? `USD ${Number(r.quoted_value_usd).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}</td>
                <td className="px-5 py-3 text-center"><StatusBadge status={r.stage || "new"} /></td>
                <td className="px-5 py-3 text-right">
                  <button onClick={(e) => remove(e, r)} className="text-slate-400 hover:text-destructive" title="Delete RFQ" data-testid={`delete-rfq-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {!rfqs.length && <tr><td colSpan={13} className="px-5 py-10 text-center text-muted-foreground">No RFQs found.</td></tr>}
          </tbody>
        </table>
      </div>

      <PaginationBar page={page} setPage={setPage} total={rfqs.length} testid="rfq-pagination" />
      </>)}

      <BulkBar
        count={sel.selected.size}
        onClear={sel.clear}
        onDelete={bulkDelete}
        canDelete={isAdmin}
        deleting={bulkBusy}
      >
        <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-edit-button">
          <Pencil className="mr-1.5 h-4 w-4" />Edit
        </Button>
      </BulkBar>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="rfq-bulk-edit-dialog">
          <DialogHeader><DialogTitle>Edit {sel.selected.size} RFQ{sel.selected.size !== 1 ? "s" : ""}</DialogTitle>
          <DialogDescription>Apply a new stage and/or owner to all selected RFQs.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-2">
              <Label>Stage</Label>
              <Select value={bulkStage} onValueChange={setBulkStage}>
                <SelectTrigger data-testid="bulk-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No change</SelectItem>
                  {RFQ_STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Owner</Label>
              <Select value={bulkOwner} onValueChange={setBulkOwner}>
                <SelectTrigger data-testid="bulk-owner-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No change</SelectItem>
                  {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name || m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={bulkApply} disabled={bulkBusy} data-testid="bulk-apply-button">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateRFQDialog
        open={open}
        onOpenChange={setOpen}
        clients={clients}
        onClientCreated={(c) => setClients((prev) => [...prev, c].sort((a, b) => a.name.localeCompare(b.name)))}
      />
      <ImportHistoryDialog open={importOpen} onOpenChange={setImportOpen} onDone={() => { setView("completed"); setRefreshKey((k) => k + 1); api.get("/clients").then((r) => setClients(r.data)); }} />
    </AppLayout>
  );
}
